import type { ReactNode } from 'react'
import '../notes.css'

/**
 * Why a control is disabled, stated next to it.
 *
 * CLAUDE.md: every disabled control states **which** reason applies. Since the
 * M0 intake cut the read-only window (master PRD D11, revised) there are two —
 * premium-gated, and pending — not three.
 *
 * The reason renders **after** the control, as real adjacent text. A disabled
 * button takes no tab stop, so a screen-reader user never lands on it: an
 * `aria-describedby` or a `title` would only be reachable by a pointer, or not
 * at all. Adjacent content is what they read next.
 *
 * This component still cannot put `aria-describedby` on the control itself —
 * it receives it as `children` and does not own it — but it CAN give the
 * reason an id for the caller to point at. **`reasonId` is optional and does
 * nothing on its own**: a caller that also wants the reason announced at
 * focus time pairs it with `aria-disabled` (not `disabled`, which drops the
 * control out of the tab order) and its own `aria-describedby={reasonId}` on
 * the control — `Household.tsx`'s leave button is the in-repo shape that
 * does both. Omitting `reasonId` still renders the reason as real adjacent
 * text, reachable by a screen reader's normal linear reading even without a
 * focus-time announcement, which is the whole reason this component renders
 * the sentence rather than hiding it in a tooltip.
 */
export function DisabledReason({
  reason,
  reasonId,
  children,
}: {
  reason: string
  reasonId?: string
  children: ReactNode
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {children}
      <span id={reasonId} role="note" className="note">
        {reason}
      </span>
    </div>
  )
}
